import { useState } from 'react'
import {
  useCreateRoom,
  useDevices,
  usePairingCode,
  useRevokeDevice,
  useRoomFutureBookings,
  useRooms,
  useUpdateRoom,
} from '../../api/queries'
import type { Device, Room } from '../../api/types'
import { Dialog } from '../../components/Dialog'
import { ErrorNotice } from '../../components/ErrorNotice'
import { Badge, Button, Empty, Field, Input, Loading, PageTitle, Switch } from '../../components/ui'
import { cn } from '../../lib/cn'
import { fmtDate, fmtRange, fmtTime } from '../../lib/time'
import { minutes, seats } from '../../lib/words'

/**
 * Экраны 5.1, 5.2, 5.5, 5.8 — конструктор переговорных.
 *
 * Заказчик просил добавлять комнаты без разработчика, поэтому здесь всё, что нужно
 * для жизни комнаты: карточка, видимость, привязка планшета и его состояние.
 *
 * Удалённого управления планшетами нет сознательно — на трёх комнатах проще подойти
 * руками. Но строка «Молчит 12 минут» стоит почти ничего и закрывает главный страх:
 * узнать о зависшей табличке от системы, а не от коллег.
 */
export function AdminRoomsPage() {
  const rooms = useRooms(true)
  const devices = useDevices()
  const setVisibility = useUpdateRoom()

  const [editing, setEditing] = useState<Room | 'new' | null>(null)
  const [pairing, setPairing] = useState<Room | null>(null)
  const [hiding, setHiding] = useState<Room | null>(null)

  const deviceByRoom = new Map<number, Device>()
  for (const device of devices.data ?? []) deviceByRoom.set(device.room_id, device)

  return (
    <>
      <PageTitle
        action={
          <Button variant="primary" onClick={() => setEditing('new')}>
            Добавить переговорную
          </Button>
        }
      >
        Переговорные
      </PageTitle>

      {rooms.error && <ErrorNotice error={rooms.error} className="mb-4" />}
      {rooms.isLoading && <Loading />}

      {rooms.data && rooms.data.length === 0 && (
        <Empty
          title="Комнат пока нет"
          hint="Заведите первую переговорную: название, вместимость и оснащение. Она сразу станет доступна всем."
        />
      )}

      <div className="flex flex-col gap-2">
        {(rooms.data ?? []).map((room) => (
          <div key={room.id} className="card flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{room.name}</span>
                <Badge>{seats(room.capacity)}</Badge>
                {/*
                  Свои часы показываем меткой в списке, а не только в карточке:
                  иначе, чтобы понять, почему комната не бронируется утром,
                  нужно открыть её и догадаться посмотреть на поле времени.
                */}
                {room.work_start_minutes !== null && room.work_end_minutes !== null && (
                  <Badge>
                    {minutesToHHMM(room.work_start_minutes)} – {minutesToHHMM(room.work_end_minutes)}
                  </Badge>
                )}
                {!room.is_visible && <Badge tone="busy">Скрыта — на ремонте</Badge>}
              </div>
              <p className="mt-0.5 text-sm text-muted">
                {room.equipment.length > 0 ? room.equipment.join(', ') : 'Оснащение не указано'}
              </p>
              <DeviceLine device={deviceByRoom.get(room.id)} />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm text-muted">Видна</span>
              <Switch
                checked={room.is_visible}
                label={`Видимость комнаты ${room.name}`}
                disabled={setVisibility.isPending}
                onChange={(next) => {
                  // Включение обратно — безобидно и делается сразу. Скрытие спрашивает
                  // про будущие брони: тихо исчезнувшая встреча плоха для участников.
                  if (next) setVisibility.mutate({ id: room.id, is_visible: true })
                  else setHiding(room)
                }}
              />
              <Button size="sm" variant="ghost" onClick={() => setPairing(room)}>
                Планшет
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(room)}>
                Изменить
              </Button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <RoomDialog room={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {pairing && (
        <PairingDialog
          room={pairing}
          device={deviceByRoom.get(pairing.id)}
          onClose={() => setPairing(null)}
        />
      )}
      {hiding && <HideRoomDialog room={hiding} onClose={() => setHiding(null)} />}
    </>
  )
}

/** Сколько минут молчания считать признаком зависшей таблички. */
const SILENT_THRESHOLD_MINUTES = 3

function DeviceLine({ device }: { device: Device | undefined }) {
  if (!device || !device.is_paired) {
    return <p className="mt-1 text-sm text-muted">Планшет не привязан</p>
  }
  // silent_minutes приходит ровно тогда, когда планшет хоть раз выходил на связь.
  if (device.silent_minutes == null || !device.last_seen_at) {
    return <p className="mt-1 text-sm text-muted">Планшет привязан, но ещё ни разу не выходил на связь</p>
  }

  // Молчание считает сервер и присылает готовым числом — сервер для этого поле
  // и завёл. Считать «сколько минут назад» здесь значит верить часам ноутбука
  // администратора: убежавшие на час часы объявят молчащими все исправные
  // таблички, и человек пойдёт перезагружать три работающих экрана.
  const alive = device.silent_minutes < SILENT_THRESHOLD_MINUTES
  return (
    <p className={cn('mt-1 text-sm', alive ? 'text-state-free' : 'text-state-busy')}>
      {alive ? 'Планшет на связи' : `Планшет молчит ${minutes(device.silent_minutes)}`}
      {!alive && (
        <span className="text-muted"> · последний раз в {fmtTime(device.last_seen_at)}, стоит подойти и перезагрузить</span>
      )}
    </p>
  )
}

/** Экран 5.2 — карточка комнаты. */
function RoomDialog({ room, onClose }: { room: Room | null; onClose: () => void }) {
  const create = useCreateRoom()
  const update = useUpdateRoom()

  const [name, setName] = useState(room?.name ?? '')
  const [capacity, setCapacity] = useState(room?.capacity ?? 6)
  const [photoUrl, setPhotoUrl] = useState(room?.photo_url ?? '')
  const [equipment, setEquipment] = useState((room?.equipment ?? []).join(', '))
  const [workStart, setWorkStart] = useState(minutesToHHMM(room?.work_start_minutes))
  const [workEnd, setWorkEnd] = useState(minutesToHHMM(room?.work_end_minutes))

  const saving = create.isPending || update.isPending
  const error = create.error ?? update.error
  // Часы задаются парой: одна половина без второй — заведомо отказ сервера,
  // и лучше не давать нажать кнопку, чем показывать ошибку после нажатия.
  const hoursPaired = (workStart === '') === (workEnd === '')
  const ready = name.trim().length > 0 && capacity > 0 && hoursPaired

  function save() {
    const payload = {
      name: name.trim(),
      capacity,
      photo_url: photoUrl.trim(),
      equipment: equipment
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      // Пустые строки шлём осознанно: так сервер отличает «снять свои часы»
      // от «не менять их». Поле, которого нет, он бы не тронул.
      work_start: workStart,
      work_end: workEnd,
    }
    if (room) update.mutate({ id: room.id, ...payload }, { onSuccess: onClose })
    else create.mutate({ ...payload, is_visible: true }, { onSuccess: onClose })
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={room ? 'Карточка переговорной' : 'Новая переговорная'}
      description={room ? undefined : 'Комната станет доступна для брони сразу после сохранения.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" disabled={!ready} loading={saving} onClick={save}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Название">
          <Input autoFocus value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Вместимость" hint="Используется в предупреждении «людей больше, чем мест».">
          <Input
            type="number"
            min={1}
            className="tnum"
            value={capacity}
            onChange={(event) => setCapacity(Number(event.target.value))}
          />
        </Field>
        <Field label="Ссылка на фотографию" hint="Показывается в карточке комнаты и на планшете.">
          <Input value={photoUrl} onChange={(event) => setPhotoUrl(event.target.value)} />
        </Field>
        <Field label="Оснащение" hint="Через запятую: Экран, Конференц-связь, Маркерная доска">
          <Input value={equipment} onChange={(event) => setEquipment(event.target.value)} />
        </Field>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">Время работы</legend>
          <div className="flex items-center gap-2">
            <Input
              type="time"
              step={1800}
              className="tnum"
              aria-label="Начало работы переговорной"
              value={workStart}
              onChange={(event) => setWorkStart(event.target.value)}
            />
            <span className="text-muted">–</span>
            <Input
              type="time"
              step={1800}
              className="tnum"
              aria-label="Конец работы переговорной"
              value={workEnd}
              onChange={(event) => setWorkEnd(event.target.value)}
            />
            {(workStart !== '' || workEnd !== '') && (
              <Button
                variant="ghost"
                onClick={() => {
                  setWorkStart('')
                  setWorkEnd('')
                }}
              >
                Очистить
              </Button>
            )}
          </div>
          <p className="hint">
            {workStart === '' && workEnd === ''
              ? 'Пусто — комната работает по общим часам из раздела «Правила».'
              : 'Эти часы заменяют общие: комната может работать и дольше офиса, и меньше.'}
          </p>
          {!hoursPaired && (
            <p className="text-sm text-state-busy">
              Укажите обе границы или очистите обе.
            </p>
          )}
        </fieldset>

        {error && <ErrorNotice error={error} />}
      </div>
    </Dialog>
  )
}

/** Экран 5.5 — разовый код привязки. Личные учётки на дверь не попадают никогда. */
function PairingDialog({
  room,
  device,
  onClose,
}: {
  room: Room
  device: Device | undefined
  onClose: () => void
}) {
  const pairingCode = usePairingCode()
  const revoke = useRevokeDevice()

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Планшет: ${room.name}`}
      description="На планшете откройте адрес системы и введите код. Устройство получит собственный токен — личная учётная запись на дверь не попадает."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
          {device?.is_paired && (
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() => revoke.mutate(device.id)}
            >
              Отозвать планшет
            </Button>
          )}
          <Button variant="primary" loading={pairingCode.isPending} onClick={() => pairingCode.mutate(room.id)}>
            Показать код привязки
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {pairingCode.data && (
          <div className="rounded-card border border-accent/30 bg-accent-soft px-4 py-5 text-center">
            <p className="tnum text-4xl font-semibold tracking-[0.15em] text-accent">
              {pairingCode.data.pairing_code}
            </p>
            <p className="mt-2 text-sm text-fg/80">
              Код действует до {fmtTime(pairingCode.data.expires_at)}. Потом нужен новый.
            </p>
          </div>
        )}

        <DeviceLine device={device} />

        <p className="hint">
          Если планшет потеряли или меняют, отзовите токен: он перестанет действовать
          немедленно, и табличка вернётся к экрану ввода кода.
        </p>

        {(pairingCode.error || revoke.error) && (
          <ErrorNotice error={pairingCode.error ?? revoke.error} />
        )}
      </div>
    </Dialog>
  )
}

/**
 * Экран 5.8 — комната на ремонте.
 *
 * Скрытие не отменяет брони само по себе: судьба чужих встреч — отдельное осознанное
 * действие, а не побочный эффект переключателя. Поэтому сначала показываем, сколько
 * их и чьи они.
 */
function HideRoomDialog({ room, onClose }: { room: Room; onClose: () => void }) {
  const future = useRoomFutureBookings(room.id, true)
  const update = useUpdateRoom()
  const [cancelFuture, setCancelFuture] = useState(false)
  const [reason, setReason] = useState('')

  const bookings = future.data ?? []
  const ready = !cancelFuture || reason.trim().length > 0

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Скрыть «${room.name}»?`}
      description="Комната пропадёт из поиска и с таймлайна. Уже созданные брони останутся, если не отменить их здесь же."
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                {
                  id: room.id,
                  is_visible: false,
                  cancel_future_bookings: cancelFuture,
                  cancel_reason: cancelFuture ? reason.trim() : undefined,
                },
                { onSuccess: onClose },
              )
            }
          >
            Скрыть комнату
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {future.isLoading && <Loading text="Смотрим будущие брони…" />}

        {bookings.length > 0 ? (
          <div>
            <p className="label">Будущих броней: {bookings.length}</p>
            <ul className="max-h-56 overflow-auto rounded-lg border border-line">
              {bookings.map((booking) => (
                <li key={booking.id} className="border-b border-line px-3 py-2 text-sm last:border-b-0">
                  <span className="font-medium">{booking.title || 'Занято'}</span>
                  <span className="tnum text-muted">
                    {' '}
                    · {fmtDate(booking.starts_at)}, {fmtRange(booking.starts_at, booking.ends_at)}
                    {booking.organizer && ` · ${booking.organizer.display_name}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          !future.isLoading && <p className="hint">Будущих броней в этой комнате нет.</p>
        )}

        {bookings.length > 0 && (
          <>
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={cancelFuture}
                onChange={(event) => setCancelFuture(event.target.checked)}
              />
              <span className="text-sm font-medium">Отменить эти брони и уведомить участников</span>
            </label>

            {cancelFuture && (
              <Field
                label="Причина отмены"
                hint="Уйдёт участникам в письме. Встреча, исчезнувшая без объяснения, — верный способ поссорить людей с системой."
              >
                <Input
                  autoFocus
                  maxLength={500}
                  value={reason}
                  placeholder="Например: в комнате меняют мебель до конца недели"
                  onChange={(event) => setReason(event.target.value)}
                />
              </Field>
            )}
          </>
        )}

        {update.error && <ErrorNotice error={update.error} />}
      </div>
    </Dialog>
  )
}

/**
 * Минуты от полуночи в «09:00» для поля ввода. null — пустая строка, то есть
 * «своих часов нет».
 *
 * Сервер отдаёт минуты, а <input type="time"> понимает только ЧЧ:ММ. Обратное
 * преобразование делает сам сервер: так формат разбирается в одном месте,
 * а не в двух по-разному.
 */
function minutesToHHMM(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return ''
  const hh = String(Math.floor(minutes / 60)).padStart(2, '0')
  const mm = String(minutes % 60).padStart(2, '0')
  return `${hh}:${mm}`
}
